import { chartInstallerMatch, installerMatchMessage, installerTag } from '@qim/shared'
import type { JSX } from 'react'

/**
 * Which installer image ran an apply (IM-195), and whether it went with the
 * chart (IM-196).
 *
 * The tag is what people compare — `9.6-20261007-3555` against a chart of the
 * same name — so it is what is shown. The full address and the digest go in
 * the title: the registry differs per environment, and a tag can be moved
 * while a digest cannot, so "which installer exactly" still has an answer for
 * whoever needs it.
 */
export const InstallerLabel = ({
  image,
  digest,
}: {
  readonly image: string
  readonly digest?: string | undefined
}): JSX.Element => (
  <code title={digest === undefined ? image : `${image}\n${digest}`}>
    {installerTag(image) ?? image}
  </code>
)

/**
 * The chart against the installer that applied it, said only when it is worth
 * saying: the ordinary case is the two agreeing, and a line on every instance
 * confirming it is a line nobody reads. Never a verdict on the instance — it
 * is running, whatever this says.
 */
export const InstallerMatchNote = ({
  chartVersion,
  image,
  region,
}: {
  readonly chartVersion: string
  readonly image: string
  readonly region: string
}): JSX.Element | null => {
  const message = installerMatchMessage(
    chartVersion,
    chartInstallerMatch(chartVersion, image),
    region,
  )
  if (message.level !== 'note' && message.level !== 'warning') return null
  return message.level === 'warning' ? (
    <span className="installer-match-warning">
      <span aria-hidden="true">!</span> Warning: {message.text}
    </span>
  ) : (
    <span className="hint">{message.text}</span>
  )
}
